/**
 * @fileoverview Hook responsável por consumir e gerenciar a Área de Treinamento
 * (Academy). Wrappers finos sobre `api`: consume (catálogo/curso/progresso) +
 * admin (CRUD/reorder/thumbnail de curso/módulo/aula).
 */

import api from "../../services/api";

const useAcademy = () => {
  /* ----------------------------- Consume ----------------------------- */
  const listCourses = async () => {
    const { data } = await api.request({ url: "/academy/courses", method: "GET" });
    return data;
  };

  const showCourse = async (id) => {
    const { data } = await api.request({ url: `/academy/courses/${id}`, method: "GET" });
    return data;
  };

  const markComplete = async (lessonId) => {
    const { data } = await api.request({
      url: `/academy/lessons/${lessonId}/complete`,
      method: "POST"
    });
    return data;
  };

  const unmarkComplete = async (lessonId) => {
    const { data } = await api.request({
      url: `/academy/lessons/${lessonId}/complete`,
      method: "DELETE"
    });
    return data;
  };

  /* ------------------------- Admin: cursos --------------------------- */
  const saveCourse = async (payload) => {
    const { data } = await api.request({ url: "/academy/courses", method: "POST", data: payload });
    return data;
  };

  const updateCourse = async (id, payload) => {
    const { data } = await api.request({ url: `/academy/courses/${id}`, method: "PUT", data: payload });
    return data;
  };

  const removeCourse = async (id) => {
    const { data } = await api.request({ url: `/academy/courses/${id}`, method: "DELETE" });
    return data;
  };

  const reorderCourses = async (orderedIds) => {
    const { data } = await api.request({
      url: "/academy/courses/reorder",
      method: "PUT",
      data: { orderedIds }
    });
    return data;
  };

  const uploadThumbnail = async (courseId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("typeArch", "academy");
    const { data } = await api.post(`/academy/courses/${courseId}/thumbnail-upload`, formData);
    return data;
  };

  const deleteThumbnail = async (courseId) => {
    const { data } = await api.request({
      url: `/academy/courses/${courseId}/thumbnail-upload`,
      method: "DELETE"
    });
    return data;
  };

  /* ------------------------- Admin: módulos -------------------------- */
  const saveModule = async (payload) => {
    const { data } = await api.request({ url: "/academy/modules", method: "POST", data: payload });
    return data;
  };

  const updateModule = async (id, payload) => {
    const { data } = await api.request({ url: `/academy/modules/${id}`, method: "PUT", data: payload });
    return data;
  };

  const removeModule = async (id) => {
    const { data } = await api.request({ url: `/academy/modules/${id}`, method: "DELETE" });
    return data;
  };

  const reorderModules = async (courseId, orderedIds) => {
    const { data } = await api.request({
      url: "/academy/modules/reorder",
      method: "PUT",
      data: { courseId, orderedIds }
    });
    return data;
  };

  /* -------------------------- Admin: aulas --------------------------- */
  const saveLesson = async (payload) => {
    const { data } = await api.request({ url: "/academy/lessons", method: "POST", data: payload });
    return data;
  };

  const updateLesson = async (id, payload) => {
    const { data } = await api.request({ url: `/academy/lessons/${id}`, method: "PUT", data: payload });
    return data;
  };

  const removeLesson = async (id) => {
    const { data } = await api.request({ url: `/academy/lessons/${id}`, method: "DELETE" });
    return data;
  };

  const reorderLessons = async (moduleId, orderedIds) => {
    const { data } = await api.request({
      url: "/academy/lessons/reorder",
      method: "PUT",
      data: { moduleId, orderedIds }
    });
    return data;
  };

  return {
    listCourses,
    showCourse,
    markComplete,
    unmarkComplete,
    saveCourse,
    updateCourse,
    removeCourse,
    reorderCourses,
    uploadThumbnail,
    deleteThumbnail,
    saveModule,
    updateModule,
    removeModule,
    reorderModules,
    saveLesson,
    updateLesson,
    removeLesson,
    reorderLessons
  };
};

export default useAcademy;
